import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Clear as ClearIcon, Search as SearchIcon, TrendingDown as TrendingDownIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridPaginationModel,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { useBulkCreateSfOpportunities } from '~/hooks/apis/price-parity/useBulkCreateSfOpportunities';
import { useGetControlCentreProperties } from '~/hooks/apis/price-parity/useGetControlCentreProperties';

import { formatDateShort } from '~/services/TimeService';

import { ControlCentreProperty, ControlCentreSummary } from '~/types/services/priceParrot';

import ControlCentreActionButtons from './ControlCentreActionButtons';
import { PROPERTY_SCORE_CATEGORY_CHIP, STATUS_CHIP_CONFIG } from './constants';
import {
  YOY_DECLINE_HIGH_THRESHOLD,
  formatCurrency,
  formatNumber,
  formatPercentage,
  getBedbankLpcSeverity,
  getImpressionsSeverity,
  getPctImpressionsSeverity,
  getPctOverpricedSeverity,
  getVnsDaysBgColor,
} from './utils';

const FILTER_ALL = 'all';
const TIER_FILTER_OPTIONS = [
  { value: FILTER_ALL, label: 'All' },
  { value: '1', label: 'Tier 1' },
  { value: '2', label: 'Tier 2' },
  { value: '3', label: 'Tier 3' },
];

const PAGE_SIZE_OPTIONS = [10, 50, 100, 200] as const;

const NONE_FILTER_VALUE = '__none__';

const TIER_CHIP_STYLES: Record<number, { bgcolor: string; color: string; borderColor?: string }> = {
  1: { bgcolor: '#fff3e0', color: '#e65100' },
  2: { bgcolor: '#e3f2fd', color: '#1565c0' },
  3: { bgcolor: '#f5f5f5', color: '#616161' },
  4: { bgcolor: '#ffffff', color: '#9e9e9e', borderColor: '#bdbdbd' },
};

const DEFAULT_SORT_FIELD = 'impressionsOverpriced';
const DEFAULT_SORT_DIRECTION = 'desc' as const;
const SEARCH_DEBOUNCE_MS = 300;

const EMPTY_SUMMARY: ControlCentreSummary = {
  propertiesFlagged: 0,
  openOpportunities: 0,
  casesCreatedToday: 0,
  avgOverpricedImpressionsPct: 0,
};

function renderPropertyScoreChip({ value }: GridCellParams) {
  if (value == null) {
    return '—';
  }
  const category = value as string;
  const config = PROPERTY_SCORE_CATEGORY_CHIP[category];
  return <Chip size="small" label={config?.label ?? category} color={config?.color ?? 'default'} variant="outlined" />;
}

function matchesStatusFilter(row: ControlCentreProperty, filter: string): boolean {
  if (filter === FILTER_ALL) {
    return true;
  }
  if (filter === NONE_FILTER_VALUE) {
    return row.connectivityActionStatus == null;
  }
  return row.connectivityActionStatus === filter;
}

export default function PriceParrotControlCentre() {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 10,
    page: 0,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: DEFAULT_SORT_FIELD, sort: DEFAULT_SORT_DIRECTION },
  ]);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [actionStatusFilter, setActionStatusFilter] = useState(FILTER_ALL);
  const [regionFilter, setRegionFilter] = useState(FILTER_ALL);
  const [propertyTierFilter, setPropertyTierFilter] = useState(FILTER_ALL);
  const [minPctImpressionsOverpriced, setMinPctImpressionsOverpriced] = useState<string>('0');
  const [minPctOverpriced, setMinPctOverpriced] = useState<string>('0');
  const [maxBedbankLpcPct, setMaxBedbankLpcPct] = useState<string>('100');
  const [appliedExtendedFilters, setAppliedExtendedFilters] = useState<{
    actionStatus?: string;
    region?: string;
    propertyTier?: number;
    minPctImpressionsOverpriced?: number;
    minPctOverpriced?: number;
    maxBedbankLpcPct?: number;
  }>({});
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState<null | HTMLElement>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const { data: allProperties = [], isLoading, error, refetch } = useGetControlCentreProperties();
  const bulkCreate = useBulkCreateSfOpportunities();

  const regionOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const prop of allProperties) {
      if (prop.offerCountryCode && !seen.has(prop.offerCountryCode)) {
        seen.set(prop.offerCountryCode, prop.offerCountryName ?? prop.offerCountryCode);
      }
    }
    const sorted = Array.from(seen.entries())
      .sort(([, a], [, b]) => a.localeCompare(b))
      .map(([code, name]) => ({ value: code, label: name }));
    return [{ value: FILTER_ALL, label: 'All' }, ...sorted];
  }, [allProperties]);

  const statusFilterOptions = useMemo(() => {
    const statuses = new Set<string>();
    let hasNull = false;
    for (const prop of allProperties) {
      if (prop.connectivityActionStatus == null) {
        hasNull = true;
      } else {
        statuses.add(prop.connectivityActionStatus);
      }
    }
    const sorted = Array.from(statuses)
      .sort((a, b) => a.localeCompare(b))
      .map((status) => ({
        value: status,
        label: STATUS_CHIP_CONFIG[status]?.label ?? status,
      }));
    return [
      { value: FILTER_ALL, label: 'All' },
      ...sorted,
      ...(hasNull ? [{ value: NONE_FILTER_VALUE, label: 'No Opp' }] : []),
    ];
  }, [allProperties]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchInput('');
    setDebouncedSearch('');
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const filteredRows = useMemo(() => {
    let rows = allProperties;

    if (debouncedSearch) {
      const term = debouncedSearch.toLowerCase();
      rows = rows.filter((r) => r.propertyName.toLowerCase().includes(term));
    }

    if (appliedExtendedFilters.actionStatus) {
      rows = rows.filter((r) => matchesStatusFilter(r, appliedExtendedFilters.actionStatus));
    }

    if (appliedExtendedFilters.region) {
      rows = rows.filter((r) => r.offerCountryCode === appliedExtendedFilters.region);
    }
    if (appliedExtendedFilters.propertyTier) {
      rows = rows.filter((r) => r.tier === appliedExtendedFilters.propertyTier);
    }
    if (appliedExtendedFilters.minPctImpressionsOverpriced != null) {
      rows = rows.filter((r) => r.pctImpressionsOverpriced >= appliedExtendedFilters.minPctImpressionsOverpriced);
    }
    if (appliedExtendedFilters.minPctOverpriced != null) {
      rows = rows.filter((r) => r.pctOverpriced >= appliedExtendedFilters.minPctOverpriced);
    }
    if (appliedExtendedFilters.maxBedbankLpcPct != null) {
      rows = rows.filter((r) => r.bedbankLpcPct == null || r.bedbankLpcPct <= appliedExtendedFilters.maxBedbankLpcPct);
    }

    return rows;
  }, [allProperties, debouncedSearch, appliedExtendedFilters]);

  const summary: ControlCentreSummary = useMemo(() => {
    if (allProperties.length === 0) {
      return EMPTY_SUMMARY;
    }
    const openCount = allProperties.filter((r) => r.openOpp).length;
    const todayStr = new Date().toISOString().slice(0, 10);
    const createdTodayCount = allProperties.filter(
      (r) => r.createdDate != null && r.createdDate.slice(0, 10) === todayStr,
    ).length;
    const avgPct = allProperties.reduce((acc, r) => acc + r.pctImpressionsOverpriced, 0) / allProperties.length;
    return {
      propertiesFlagged: allProperties.length,
      openOpportunities: openCount,
      casesCreatedToday: createdTodayCount,
      avgOverpricedImpressionsPct: Math.round(avgPct * 10) / 10,
    };
  }, [allProperties]);

  useEffect(() => {
    if (allProperties.length > 0 && !error) {
      setLastRefreshed(new Date());
    }
  }, [allProperties, error]);

  const handleApplyFilters = useCallback(() => {
    const next = {
      actionStatus: actionStatusFilter !== FILTER_ALL ? actionStatusFilter : undefined,
      region: regionFilter && regionFilter !== FILTER_ALL ? regionFilter : undefined,
      propertyTier: propertyTierFilter && propertyTierFilter !== FILTER_ALL ? Number(propertyTierFilter) : undefined,
      minPctImpressionsOverpriced: minPctImpressionsOverpriced ? Number(minPctImpressionsOverpriced) : undefined,
      minPctOverpriced: minPctOverpriced ? Number(minPctOverpriced) : undefined,
      maxBedbankLpcPct: maxBedbankLpcPct ? Number(maxBedbankLpcPct) : undefined,
    };
    setAppliedExtendedFilters(next);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [
    actionStatusFilter,
    regionFilter,
    propertyTierFilter,
    minPctImpressionsOverpriced,
    minPctOverpriced,
    maxBedbankLpcPct,
  ]);

  const handleResetFilters = useCallback(() => {
    setSearchInput('');
    setDebouncedSearch('');
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    setActionStatusFilter(FILTER_ALL);
    setRegionFilter(FILTER_ALL);
    setPropertyTierFilter(FILTER_ALL);
    setMinPctImpressionsOverpriced('0');
    setMinPctOverpriced('0');
    setMaxBedbankLpcPct('100');
    setAppliedExtendedFilters({});
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleBulkCreateCases = useCallback(() => {
    setBulkMenuAnchor(null);
    const selectedIds = Array.isArray(rowSelectionModel) ? rowSelectionModel : [];
    if (selectedIds.length === 0) {
      return;
    }

    const items = allProperties
      .filter((r) => selectedIds.includes(r.propertyId) && r.vendorBk != null)
      .map((r) => ({ offer_bk: r.offerBk, vendor_bk: r.vendorBk }));

    if (items.length === 0) {
      return;
    }
    bulkCreate.mutate({ items, reason: 'Parity Review' });
  }, [rowSelectionModel, allProperties, bulkCreate]);

  const columns = useMemo<Array<GridColDef<ControlCentreProperty>>>(
    () => [
      {
        field: 'propertyName',
        headerName: 'Property Name',
        flex: 2,
        minWidth: 200,
        sortable: false,
        renderCell: ({ row }) =>
          row.vendorBk ? (
            <Link href={`/vendors/${row.vendorBk}/properties/${row.propertyId}`} underline="hover">
              {row.propertyName}
            </Link>
          ) : (
            row.propertyName
          ),
      },
      {
        field: 'openOpp',
        headerName: 'Open Opp',
        width: 100,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value }) => {
          const yes = value === true || (value as unknown) === 'true';
          return <Chip size="small" label={yes ? 'Yes' : 'No'} color={yes ? 'success' : 'default'} />;
        },
      },
      {
        field: 'connectivityActionStatus',
        headerName: 'Status',
        width: 140,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value }) => {
          if (!value) {
            return <Chip size="small" label="No Opp" color="default" variant="outlined" />;
          }
          const config = STATUS_CHIP_CONFIG[value as string];
          return (
            <Chip
              size="small"
              label={config?.label ?? (value as string)}
              color={config?.color ?? 'default'}
              variant="outlined"
            />
          );
        },
      },
      {
        // VNS = "Views, No Sales" — count of days in the trailing 30-day window
        // where an offer had page views but zero orders.
        field: 'vnsDays7d',
        headerName: 'VNS Days (30D)',
        width: 120,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => {
          const days = row.vnsDays7d;
          if (days == null) {
            return '—';
          }
          const bg = getVnsDaysBgColor(days);
          return (
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              {days}
            </Box>
          );
        },
      },
      {
        field: 'impressionsOverpriced',
        headerName: '# Overpriced Impr (L30D)',
        width: 180,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: formatNumber,
        cellClassName: (params: GridCellParams) => getImpressionsSeverity(params.value as number),
      },
      {
        field: 'pctImpressionsOverpriced',
        headerName: '% Impr. Overpriced',
        width: 160,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: formatPercentage,
        cellClassName: (params: GridCellParams) => getPctImpressionsSeverity(params.value as number),
      },
      {
        field: 'pctOverpriced',
        headerName: '% Overpriced',
        width: 130,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: formatPercentage,
        cellClassName: (params: GridCellParams) => getPctOverpricedSeverity(params.value as number),
      },
      {
        field: 'propertyScore7d',
        headerName: 'Property Score (L7D)',
        width: 150,
        align: 'center',
        headerAlign: 'center',
        renderCell: renderPropertyScoreChip,
      },
      {
        field: 'propertyScore30d',
        headerName: 'Property Score (L30D)',
        width: 160,
        align: 'center',
        headerAlign: 'center',
        renderCell: renderPropertyScoreChip,
      },
      {
        field: 'bedbankLpcPct',
        headerName: 'Bedbank:LPC',
        width: 120,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: (value: number | null | undefined) => (value != null ? formatPercentage(value) : '—'),
        cellClassName: (params: GridCellParams) => getBedbankLpcSeverity(params.value as number | null | undefined),
      },
      {
        field: 'yoyDeclinePct',
        headerName: 'YoY Decline',
        width: 120,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value }) => {
          if (value == null) {
            return '—';
          }
          const pct = value as number;
          const isHigh = pct >= YOY_DECLINE_HIGH_THRESHOLD;
          return (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <TrendingDownIcon fontSize="small" sx={{ color: 'error.main' }} />
              <Typography variant="body2" sx={isHigh ? { color: 'error.main', fontWeight: 600 } : undefined}>
                {formatPercentage(pct)}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: 'mpv7d',
        headerName: 'MpV (L7D)',
        width: 110,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: formatCurrency,
      },
      {
        field: 'mpv30d',
        headerName: 'MpV (L30D)',
        width: 110,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: formatCurrency,
      },
      {
        field: 'tier',
        headerName: 'Tier',
        width: 90,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value }) => {
          if (value == null) {
            return '—';
          }
          const tier = value as number;
          const style = TIER_CHIP_STYLES[tier];
          return (
            <Chip
              size="small"
              label={`Tier ${tier}`}
              sx={
                style
                  ? {
                      bgcolor: style.bgcolor,
                      color: style.color,
                      border: style.borderColor ? `1px solid ${style.borderColor}` : undefined,
                    }
                  : undefined
              }
            />
          );
        },
      },
      {
        field: 'lastActivityDate',
        headerName: 'Last Activity',
        width: 130,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: (value: string | null) => (value ? formatDateShort(value) : '—'),
      },
      {
        field: 'closedDate',
        headerName: 'Closed Date',
        width: 130,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: (value: string | null) => (value ? formatDateShort(value) : '—'),
      },
      {
        field: 'actions',
        headerName: 'Action',
        minWidth: 380,
        width: 380,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <ControlCentreActionButtons
            propertyId={row.propertyId}
            propertyName={row.propertyName}
            vendorBk={row.vendorBk}
            connectivityActionStatus={row.connectivityActionStatus}
          />
        ),
      },
    ],
    [],
  );

  const selectedCount = Array.isArray(rowSelectionModel) ? rowSelectionModel.length : 0;

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 5 }} useFlexGap flexWrap="wrap">
        <Card variant="outlined" sx={{ minWidth: 160, flex: 1 }}>
          <CardContent sx={{ '&:last-child': { pb: 2 } }}>
            <Typography variant="body2" color="text.secondary">
              Properties Flagged
            </Typography>
            {/* Summary number size defined here (fontSize) so it is not overridden by theme typography */}
            {isLoading ? (
              <CircularProgress size={28} sx={{ mt: 0.5 }} />
            ) : (
              <Typography
                component="span"
                sx={{ color: 'primary.main', fontWeight: 600, fontSize: '2rem', lineHeight: 1.2 }}
              >
                {summary.propertiesFlagged}
              </Typography>
            )}
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ minWidth: 160, flex: 1 }}>
          <CardContent sx={{ '&:last-child': { pb: 2 } }}>
            <Typography variant="body2" color="text.secondary">
              Open Opportunities
            </Typography>
            {isLoading ? (
              <CircularProgress size={28} sx={{ mt: 0.5 }} />
            ) : (
              <Typography
                component="span"
                sx={{ color: '#ed6c02', fontWeight: 600, fontSize: '2rem', lineHeight: 1.2 }}
              >
                {summary.openOpportunities}
              </Typography>
            )}
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ minWidth: 160, flex: 1 }}>
          <CardContent sx={{ '&:last-child': { pb: 2 } }}>
            <Typography variant="body2" color="text.secondary">
              Cases Created Today
            </Typography>
            {isLoading ? (
              <CircularProgress size={28} sx={{ mt: 0.5 }} />
            ) : (
              <Typography
                component="span"
                sx={{ color: 'success.main', fontWeight: 600, fontSize: '2rem', lineHeight: 1.2 }}
              >
                {summary.casesCreatedToday}
              </Typography>
            )}
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ minWidth: 160, flex: 1 }}>
          <CardContent sx={{ '&:last-child': { pb: 2 } }}>
            <Typography variant="body2" color="text.secondary">
              Avg Overpriced Impressions
            </Typography>
            {isLoading ? (
              <CircularProgress size={28} sx={{ mt: 0.5 }} />
            ) : (
              <Typography
                component="span"
                sx={{ color: 'error.main', fontWeight: 600, fontSize: '2rem', lineHeight: 1.2 }}
              >
                {summary.avgOverpricedImpressionsPct}%
              </Typography>
            )}
          </CardContent>
        </Card>
      </Stack>

      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField
            placeholder="Search by property name..."
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Stack>

        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
          <TextField
            select
            label="Status"
            value={actionStatusFilter}
            onChange={(e) => setActionStatusFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 120 }}
          >
            {statusFilterOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Region"
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 100 }}
            SelectProps={{
              renderValue: (val) => (val === FILTER_ALL ? 'All' : String(val)),
            }}
          >
            {regionOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Property Tier"
            value={propertyTierFilter}
            onChange={(e) => setPropertyTierFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 120 }}
          >
            {TIER_FILTER_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Min Overpriced Impr %"
            type="number"
            value={minPctImpressionsOverpriced}
            onChange={(e) => setMinPctImpressionsOverpriced(e.target.value)}
            size="small"
            inputProps={{ min: 0, max: 100, step: 0.1 }}
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="Min Overpriced %"
            type="number"
            value={minPctOverpriced}
            onChange={(e) => setMinPctOverpriced(e.target.value)}
            size="small"
            inputProps={{ min: 0, max: 100, step: 0.1 }}
            sx={{ minWidth: 160 }}
          />
          <TextField
            label="Max Bedbank:LPC %"
            type="number"
            value={maxBedbankLpcPct}
            onChange={(e) => setMaxBedbankLpcPct(e.target.value)}
            size="small"
            inputProps={{ min: 0, max: 100, step: 0.1 }}
            sx={{ minWidth: 180 }}
          />
          <Button variant="contained" color="primary" size="small" onClick={handleApplyFilters}>
            Apply Filters
          </Button>
          <Button variant="text" size="small" onClick={handleResetFilters}>
            Reset
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Failed to load control centre data: {error.message}
        </Alert>
      )}

      <DataGrid
        autoHeight
        columns={columns}
        rows={filteredRows}
        loading={isLoading}
        getRowId={(row) => row.propertyId}
        getRowHeight={() => 'auto'}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        slots={{ pagination: GridPagination }}
        checkboxSelection
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={setRowSelectionModel}
        disableRowSelectionOnClick
        disableColumnMenu
        sx={{
          '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
          '& .metric-danger': { bgcolor: '#ffebee' },
          '& .metric-warning': { bgcolor: '#fff3e0' },
          '& .metric-good': { bgcolor: '#e8f5e9' },
        }}
      />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1, py: 1, minHeight: 32 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            variant="outlined"
            size="small"
            disabled={selectedCount === 0}
            onClick={(e) => setBulkMenuAnchor(e.currentTarget)}
            aria-haspopup="true"
            aria-expanded={Boolean(bulkMenuAnchor)}
          >
            Bulk Actions
          </Button>
          <Menu
            anchorEl={bulkMenuAnchor}
            open={Boolean(bulkMenuAnchor)}
            onClose={() => setBulkMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <MenuItem onClick={handleBulkCreateCases}>Create SF Cases for Selected</MenuItem>
          </Menu>
        </Stack>
        <Box>
          {lastRefreshed && (
            <Typography variant="caption" color="text.secondary">
              Last refreshed: {lastRefreshed.toLocaleString()}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
